import type {ReactElement} from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {showMessageGrowl} from '@Components/message-growl';
import {GROWL_TYPE} from '@Components/message-growl/message-growl.types';
import {VersionHistoryList} from '@Components/version-history-list';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {geCurrentUserData} from '@Libraries/user.library';
import {getTimeAndDateLabel} from '@Utils/date.util';
import {commitRestore, exitVersionHistoryMode, fetchEmailVersionContent, fetchEmailVersions, persistRestoredTemplate, previewVersion, revertToCurrentVersion} from './email-maker-version-history-panel.library';
import {EmailVersionLoadingOverlay} from './components/email-version-loading-overlay/email-version-loading-overlay';
import type {EmailMakerVersionHistoryPanelProps, EmailVersionData} from './email-maker-version-history-panel.types';
import styles from './email-maker-version-history-panel.module.scss';

export function EmailMakerVersionHistoryPanel({editor, idEmailTemplate, onExit}: EmailMakerVersionHistoryPanelProps): ReactElement {
  const [versions, setVersions] = useState<EmailVersionData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadingVersionIndex, setLoadingVersionIndex] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isFetchingVersions, setIsFetchingVersions] = useState(true);
  const [currentUserName, setCurrentUserName] = useState(window.i18next.t('pmwjs_you'));
  const [openedAtTimestamp] = useState(() => Math.floor(Date.now() / 1000));
  const listRef = useRef<HTMLDivElement>(null);
  const versionContentCacheRef = useRef<Map<number, string>>(new Map());
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(listRef, !isFetchingVersions);

  useEffect(() => {
    const fetchVersions = async (): Promise<void> => {
      setIsFetchingVersions(true);
      try {
        setVersions(await fetchEmailVersions(idEmailTemplate));
      } catch (err) {
        console.error('Failed to load email version history:', err);
      } finally {
        setIsFetchingVersions(false);
      }
    };

    void fetchVersions();
  }, [idEmailTemplate]);

  useEffect(() => {
    const fetchCurrentUserName = async (): Promise<void> => {
      try {
        const userData = await geCurrentUserData();
        if (userData?.fullName) {
          setCurrentUserName(userData.fullName);
        }
      } catch (err) {
        console.error('Failed to load current user data for version history:', err);
      }
    };

    void fetchCurrentUserName();
  }, []);

  const versionHistoryListItems = useMemo(
    () => [
      {id: 'current-version', timestamp: openedAtTimestamp, authorName: currentUserName},
      ...versions.map((version) => ({id: String(version.id), timestamp: version.lastModifiedOn, authorName: version.editorName})),
    ],
    [versions, currentUserName, openedAtTimestamp]
  );

  const getVersionForListIndex = useCallback(
    (index: number): EmailVersionData | null => {
      return index > 0 ? (versions[index - 1] ?? null) : null;
    },
    [versions]
  );

  const selectCurrentVersion = useCallback((): void => {
    setSelectedIndex(0);
    try {
      revertToCurrentVersion(editor);
    } catch (err) {
      console.error('Failed to revert to the current email version:', err);
    }
  }, [editor]);

  const selectOldVersion = useCallback(
    async (index: number): Promise<void> => {
      const version = getVersionForListIndex(index);
      if (!version) {
        return;
      }

      try {
        setLoadingVersionIndex(index);
        let jsonContent = versionContentCacheRef.current.get(version.id);
        if (jsonContent === undefined) {
          jsonContent = await fetchEmailVersionContent(idEmailTemplate, version.id);
          versionContentCacheRef.current.set(version.id, jsonContent);
        }
        if (!window.PMW.emails.IS_IN_VERSION_HISTORY_MODE) {
          return;
        }
        setSelectedIndex(index);
        previewVersion(editor, jsonContent);
      } catch (err) {
        console.error('Failed to preview email version:', err);
      } finally {
        setLoadingVersionIndex(null);
      }
    },
    [editor, idEmailTemplate, getVersionForListIndex]
  );

  const handleVersionSelect = useCallback(
    async (index: number): Promise<void> => {
      if (index === selectedIndex || loadingVersionIndex !== null) {
        return;
      }

      if (index === 0) {
        selectCurrentVersion();
        return;
      }

      await selectOldVersion(index);
    },
    [selectedIndex, loadingVersionIndex, selectCurrentVersion, selectOldVersion]
  );

  const renderCanvasLoadingOverlay = (): ReactElement | null => {
    if (loadingVersionIndex === null) {
      return null;
    }
    const frameWrapper = editor.Canvas.getFrameEl()?.parentElement;
    const loadingVersion = getVersionForListIndex(loadingVersionIndex);
    if (!frameWrapper || !loadingVersion) {
      return null;
    }
    return createPortal(<EmailVersionLoadingOverlay subtitle={getTimeAndDateLabel(loadingVersion.lastModifiedOn)} />, frameWrapper);
  };

  const renderVersionHistoryList = (): ReactElement => {
    return (
      <div className={`${styles.listWrapper} ${topShadow ? styles.listTopShadow : ''} ${bottomShadow ? styles.listBottomShadow : ''}`}>
        <div ref={listRef} className={styles.listContainer}>
          <VersionHistoryList
            versions={versionHistoryListItems}
            selectedIndex={selectedIndex}
            isFetchingVersions={isFetchingVersions}
            loadingIndex={loadingVersionIndex}
            onVersionSelect={handleVersionSelect}
          />
        </div>
      </div>
    );
  };

  const handleRestore = (): void => {
    const selectedVersion = getVersionForListIndex(selectedIndex);
    if (!selectedVersion) {
      return;
    }

    setIsRestoring(true);
    try {
      commitRestore(editor);
      exitVersionHistoryMode(editor);
      persistRestoredTemplate(editor);
      onExit();
      showMessageGrowl({
        text: window.i18next.t('pmwjs_version_restore_snackbar_text'),
        type: GROWL_TYPE.NEUTRAL,
        interval: 3000,
        showCloseIcon: false,
      });
    } catch (err) {
      console.error('Failed to restore email version:', err);
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDuplicate = (): void => {
    const copyUrl = window.PMW.util.site_url(`emailmaker/copy/${idEmailTemplate}`);
    const selectedVersion = getVersionForListIndex(selectedIndex);
    window.open(selectedVersion ? `${copyUrl}?version=${selectedVersion.id}` : copyUrl, '_blank');
  };

  const renderPanelFooter = (): ReactElement => {
    return (
      <div className={`flexbox flex-items-center ${styles.footer}`}>
        <Button
          type={Type.SECONDARY}
          size={Size.SMALL}
          iconPlacement={IconPlacement.LEFT}
          icon="icon-copy"
          text={window.i18next.t('pmwjs_duplicate')}
          onClick={handleDuplicate}
          disabled={isFetchingVersions || versions.length === 0}
          customClasses="flex-1"
        />
        <Button
          type={Type.PRIMARY}
          size={Size.SMALL}
          iconPlacement={IconPlacement.LEFT}
          icon="icon-rotate-ccw"
          text={window.i18next.t('pmwjs_restore')}
          onClick={handleRestore}
          disabled={isFetchingVersions || selectedIndex === 0 || versions.length === 0}
          isLoading={isRestoring}
          customClasses="flex-1"
        />
      </div>
    );
  };

  return (
      <div className={`flexbox flex-column-justify-start ${styles.panel}`}>
        {renderVersionHistoryList()}
        {renderPanelFooter()}
        {renderCanvasLoadingOverlay()}
      </div>
  );
}
